import React, { useState, useEffect } from 'react';
import { certificatesApi } from '../api';
import { toast } from 'sonner';
import {
  BadgeCheck,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  XCircle,
  Award,
  Calendar,
  CalendarClock,
} from 'lucide-react';

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    date: '',
    expiry_date: '',
    is_active: true,
  });

  useEffect(() => {
    loadCertificates();
  }, []);

  const loadCertificates = async () => {
    try {
      setLoading(true);
      const response = await certificatesApi.list();
      setCertificates(response.data || []);
    } catch (error) {
      toast.error('Erro ao carregar certificações');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (certificate = null) => {
    if (certificate) {
      setEditingCertificate(certificate);
      setFormData({
        code: certificate.code || '',
        name: certificate.name || '',
        description: certificate.description || '',
        date: certificate.date || '',
        expiry_date: certificate.expiry_date || '',
        is_active: certificate.is_active !== false,
      });
    } else {
      setEditingCertificate(null);
      setFormData({
        code: '',
        name: '',
        description: '',
        date: '',
        expiry_date: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCertificate(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('O nome da certificação é obrigatório');
      return;
    }

    if (formData.date && formData.expiry_date && formData.expiry_date < formData.date) {
      toast.error('A data de vencimento não pode ser anterior à data da certificação');
      return;
    }

    setSaving(true);

    const cleanedData = {
      ...formData,
      code: formData.code.trim() || null,
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      date: formData.date || null,
      expiry_date: formData.expiry_date || null,
    };

    try {
      if (editingCertificate) {
        await certificatesApi.update(editingCertificate.id, cleanedData);
        toast.success('Certificação atualizada com sucesso!');
      } else {
        await certificatesApi.create(cleanedData);
        toast.success('Certificação cadastrada com sucesso!');
      }
      handleCloseModal();
      loadCertificates();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail)
        ? detail.map((e) => e.msg || e.message || JSON.stringify(e)).join(', ')
        : detail || 'Erro ao salvar certificação';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (certificate) => {
    if (!window.confirm(`Desativar a certificação "${certificate.name}"?`)) return;

    try {
      await certificatesApi.delete(certificate.id);
      toast.success('Certificação desativada com sucesso!');
      loadCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar certificação');
    }
  };

  const handleReactivate = async (certificate) => {
    try {
      await certificatesApi.reactivate(certificate.id);
      toast.success('Certificação reativada com sucesso!');
      loadCertificates();
    } catch (error) {
      toast.error('Erro ao reativar certificação');
    }
  };

  const filteredCertificates = certificates.filter((c) =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const activeCertificates = filteredCertificates.filter((c) => c.is_active);
  const inactiveCertificates = filteredCertificates.filter((c) => !c.is_active);
  const expiredCertificates = filteredCertificates.filter((c) =>
    c.expiry_date && new Date(c.expiry_date) < new Date(new Date().toISOString().slice(0, 10))
  );

  const formatDate = (date) => (date ? new Date(date).toLocaleDateString('pt-BR') : '-');

  return (
    <div className="space-y-6" data-testid="certificates-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BadgeCheck className="text-green-400" />
            Cadastro de Certificações
          </h1>
          <p className="text-gray-400 mt-1">Gerencie as certificações utilizadas nos itens de auditoria</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-primary" data-testid="new-certificate-btn">
          <Plus size={18} />
          Nova Certificação
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Buscar por nome ou código..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input w-full pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Award className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total</p>
              <p className="text-2xl font-bold text-blue-400">{filteredCertificates.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="text-green-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Ativas</p>
              <p className="text-2xl font-bold text-green-400">{activeCertificates.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <XCircle className="text-red-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Inativas</p>
              <p className="text-2xl font-bold text-red-400">{inactiveCertificates.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <CalendarClock className="text-yellow-400" size={24} />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Vencidas</p>
              <p className="text-2xl font-bold text-yellow-400">{expiredCertificates.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Código</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Vencimento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredCertificates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    Nenhuma certificação encontrada
                  </td>
                </tr>
              ) : (
                filteredCertificates.map((certificate) => {
                  const isExpired = certificate.expiry_date && new Date(certificate.expiry_date) < new Date(new Date().toISOString().slice(0, 10));
                  return (
                    <tr key={certificate.id} className="hover:bg-gray-800/50" data-testid={`certificate-row-${certificate.id}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-green-400">{certificate.code}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-white">{certificate.name}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 max-w-xs truncate">
                        {certificate.description || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-300">{formatDate(certificate.date)}</td>
                      <td className="px-4 py-3">
                        {certificate.expiry_date ? (
                          <span className={`text-sm ${isExpired ? 'text-red-400' : 'text-gray-300'}`}>
                            {formatDate(certificate.expiry_date)}
                          </span>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!certificate.is_active ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 flex items-center gap-1 w-fit">
                            <XCircle size={12} />
                            Inativa
                          </span>
                        ) : isExpired ? (
                          <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400 flex items-center gap-1 w-fit">
                            <CalendarClock size={12} />
                            Vencida
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1 w-fit">
                            <CheckCircle size={12} />
                            Ativa
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenModal(certificate)}
                            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg"
                            title="Editar"
                          >
                            <Edit size={18} />
                          </button>
                          {certificate.is_active ? (
                            <button
                              onClick={() => handleDelete(certificate)}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                              title="Desativar"
                            >
                              <Trash2 size={18} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivate(certificate)}
                              className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg"
                              title="Reativar"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-semibold text-white">
                {editingCertificate ? 'Editar Certificação' : 'Nova Certificação'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Código</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="CERT-0001 (auto)"
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Nome <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: ISO 9001:2015"
                    className="input w-full"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <Calendar size={14} className="inline mr-1" />
                    Data
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    <CalendarClock size={14} className="inline mr-1" />
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    value={formData.expiry_date}
                    onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição da certificação..."
                  className="input w-full"
                  rows={2}
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-green-500"
                  />
                  <span className="text-sm font-medium text-gray-300">Certificação Ativa</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary" disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
                  {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certificates;